export interface SubmissionErrorResponse {
  success?: boolean;
  error?: string;
  errorCode?: string;
  message?: string;
  validationErrors?: string[];
  pendingJiraCreation?: boolean;
}

const ERROR_CODE_HINTS: Record<string, string> = {
  SUB_001: 'Required submission data was missing.',
  SUB_002: 'Your project could not be found.',
  SUB_003: 'Your project is still being linked to our system.',
  SUB_004: 'Some answers need to be corrected before we can submit.',
  SUB_005: 'Your answers were saved, but the handoff to our team did not finish.',
  SUB_006: 'Your answers were saved, but syncing to our tracking system failed.',
  SUB_007: 'Your answers were synced, but updating project status failed.',
  SUB_099: 'An unexpected error occurred.',
};

function formatValidationList(errors: string[]): string {
  if (errors.length === 0) return '';
  if (errors.length === 1) return errors[0];
  return errors.map((e) => `• ${e}`).join('\n');
}

/**
 * Turn a submitFormData API error body into toast title + message for customers.
 */
export function formatSubmissionErrorForUser(
  data: SubmissionErrorResponse | null | undefined,
  fallbackMessage = 'Failed to submit your application. Please try again.'
): { title: string; message: string } {
  const errorCode = data?.errorCode;
  const baseMessage = data?.message || data?.error || fallbackMessage;
  const validationErrors = data?.validationErrors;

  let message = baseMessage;

  if (validationErrors && validationErrors.length > 0) {
    const list = formatValidationList(validationErrors);
    message = `${baseMessage}\n\n${list}`;
  }

  if (errorCode) {
    const hint = ERROR_CODE_HINTS[errorCode];
    message = `${message}\n\nReference: ${errorCode}${hint ? ` — ${hint}` : ''}`;
  }

  if (data?.pendingJiraCreation) {
    message = `${message}\n\nYou can continue editing the form while setup completes.`;
  }

  const title =
    errorCode === 'SUB_004'
      ? 'Please Review Your Form'
      : errorCode === 'SUB_003'
        ? 'Setup In Progress'
        : 'Submission Failed';

  return { title, message };
}
